import axios from "axios";
import moment from "moment";

// Catalog and filter dropdowns rarely change; fetch once per login.
const cache = {};
function cached(name, url) {
  const key = `${name}:${localStorage.getItem("token")}:${localStorage.getItem("company-id")}`;
  if (!cache[key]) {
    cache[key] = axios.get(url).then((r) => r.data.data).catch((e) => {
      delete cache[key];
      throw e;
    });
  }
  return cache[key];
}
export const loadCatalog = () => cached("catalog", "reports/catalog");
export const loadFilterOptions = () => cached("filters", "reports/filters");

export const FILTER_KEYS =["preset", "from", "to", "zone_id", "franchisee_id", "city", "pincode"];

/** Route query -> filter object (defaults to This Month). */
export function filtersFromQuery(query = {}) {
  const filters = { preset: "this_month", from: null, to: null, zone_id: null, franchisee_id: null, city: null, pincode: null };
  FILTER_KEYS.forEach((key) => {
    if (query[key] !== undefined && query[key] !== "") {
      filters[key] = ["zone_id", "franchisee_id"].includes(key) ? Number(query[key]) : query[key];
    }
  });
  return filters;
}

/** Filter object -> query / request params, without empty values. */
export function filtersToQuery(filters = {}) {
  const query = {};
  FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (value === null || value === undefined || value === "") return;
    if ((key === "from" || key === "to") && filters.preset !== "custom") return;
    query[key] = value;
  });
  return query;
}

const inr = (value, digits = 2) =>
  Number(value).toLocaleString("en-IN", { minimumFractionDigits: digits, maximumFractionDigits: digits });

/** Display formatting of a report value by column / KPI type. `plain` avoids the rupee glyph (PDF fonts lack it). */
export function formatValue(value, type, plain = false) {
  if (value === null || value === undefined || value === "") return "-";
  const isNumber = typeof value === "number" || (typeof value === "string" && value.trim() !== "" && !isNaN(value));
  switch (type) {
    case "currency": {
      if (!isNumber) return value;
      const amount = Math.abs(Number(value)) < 0.005 ? 0 : Number(value);
      return (amount < 0 ? "-" : "") + (plain ? "Rs " : "₹") + inr(Math.abs(amount));
    }
    case "percent":
      return isNumber ? `${Number(value).toFixed(2).replace(/\.?0+$/, "")}%` : value;
    case "rating":
      return isNumber ? Number(value).toFixed(1) : value;
    case "number":
      if (!isNumber) return value;
      return Number.isInteger(Number(value)) ? Number(value).toLocaleString("en-IN") : inr(value);
    case "date":
      return moment(value, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss"], true).isValid()
        ? moment(value, ["YYYY-MM-DD", "YYYY-MM-DD HH:mm:ss"]).format("DD MMM YYYY")
        : value;
    case "datetime":
      return moment(value).isValid() ? moment(value).format("DD MMM YYYY, hh:mm A") : value;
    default:
      return value;
  }
}

export const NUMERIC_TYPES = ["number", "currency", "percent", "rating"];

export function fileNameFrom(disposition, fallback) {
  const match = /filename="?([^";]+)"?/i.exec(disposition || "");
  return match ? match[1] : fallback;
}

export function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

/** Excel is generated by the API, the same file the email sends. */
export async function downloadExcel(key, filters, title) {
  const response = await axios.get(`reports/${key}/export`, {
    params: filtersToQuery(filters),
    responseType: "blob",
  });
  saveBlob(response.data, fileNameFrom(response.headers["content-disposition"], `${title}.xlsx`));
}

/** PDF built in the browser from the loaded report. */
export async function downloadPdf(report, metaLines) {
  const [{ jsPDF }, autoTableModule] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const autoTable = autoTableModule.default || autoTableModule.autoTable;

  const wide = report.columns.length > 6;
  const doc = new jsPDF({ orientation: wide ? "landscape" : "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const brand = [31, 79, 163];

  doc.setFillColor(...brand);
  doc.rect(0, 0, pageWidth, 54, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(report.report.title, 32, 34);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text("Walkies Reports", pageWidth - 32, 34, { align: "right" });

  doc.setTextColor(80, 90, 110);
  let y = 76;
  metaLines.forEach((line) => {
    doc.text(line, 32, y);
    y += 13;
  });

  if (report.summary && report.summary.length) {
    autoTable(doc, {
      startY: y + 4,
      head: [report.summary.map((k) => k.label)],
      body: [report.summary.map((k) => formatValue(k.value, k.type, true))],
      theme: "plain",
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { textColor: [102, 115, 138], fontStyle: "normal" },
      bodyStyles: { fontStyle: "bold", textColor: brand, fontSize: 11 },
      margin: { left: 32, right: 32 },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  autoTable(doc, {
    startY: y + 4,
    // Currency columns carry "(Rs)" in the header so cells stay short.
    head: [report.columns.map((c) => (c.type === "currency" ? `${c.label} (Rs)` : c.label))],
    body: report.rows.map((row) =>
      report.columns.map((c) => String(formatValue(row[c.key], c.type, true)).replace(/^(-?)Rs /, "$1"))
    ),
    styles: { fontSize: wide ? 7 : 8, cellPadding: 3, overflow: "linebreak" },
    headStyles: { fillColor: brand, textColor: 255 },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    columnStyles: report.columns.reduce((acc, c, i) => {
      if (NUMERIC_TYPES.includes(c.type)) acc[i] = { halign: "right", minCellWidth: c.type === "currency" ? 46 : 28 };
      if (c.type === "date") acc[i] = { minCellWidth: 52 };
      return acc;
    }, {}),
    margin: { left: 32, right: 32 },
    didDrawPage: () => {
      const page = doc.internal.getNumberOfPages();
      doc.setFontSize(8);
      doc.setTextColor(140, 150, 170);
      doc.text(`Page ${page}`, pageWidth - 32, doc.internal.pageSize.getHeight() - 16, { align: "right" });
    },
  });

  if (report.notes && report.notes.length) {
    let ny = doc.lastAutoTable.finalY + 18;
    doc.setFontSize(8);
    doc.setTextColor(102, 115, 138);
    report.notes.forEach((note) => {
      const lines = doc.splitTextToSize(`* ${note}`, pageWidth - 64);
      if (ny + lines.length * 10 > doc.internal.pageSize.getHeight() - 30) {
        doc.addPage();
        ny = 40;
      }
      doc.text(lines, 32, ny);
      ny += lines.length * 10 + 4;
    });
  }

  const name = report.report.title.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
  doc.save(`${name}-${report.filters.from}.pdf`);
}
